/**
 * T036: Project Management Service for User Story 2
 * 
 * Handles API communication for project management workflow
 * Constitutional compliance: <500ms API responses, enterprise error handling
 */

import { useCallback } from 'react';

export interface Project {
  id: number;
  client_id: number;
  onboarding_session_id?: number;
  project_manager_id?: number;
  name: string;
  description: string;
  project_types: string[];
  status: string;
  priority: string;
  budget_range?: string;
  timeline?: string;
  expected_start_date?: string;
  actual_start_date?: string;
  expected_end_date?: string;
  actual_end_date?: string;
  completion_percentage: number;
  technical_requirements?: any;
  business_requirements?: any;
  notes?: string;
  project_metadata?: any;
  created_at: string;
  updated_at: string;
  
  // Relationships
  client?: {
    id: number;
    name: string;
    email: string;
    company_name?: string;
  };
  project_manager?: {
    id: number;
    name: string;
    email: string;
  };
  onboarding_session?: {
    id: number;
    session_key: string;
    status: string;
    completion_percentage: number;
    completed_at?: string;
  };
  
  // Computed fields
  status_label: string;
  priority_label: string;
  budget_label?: string;
  timeline_label?: string;
  is_active: boolean;
  is_overdue: boolean;
  days_remaining?: number;
  can_be_edited: boolean;
  can_be_deleted: boolean;
  available_transitions: string[];
  
  // Progress tracking
  progress: {
    completion_percentage: number;
    status: string;
    milestones_completed: number;
    estimated_completion_date?: string;
  };
}

export interface ProjectFilters {
  status?: string;
  priority?: string;
  budget_range?: string;
  sort_by?: string;
  sort_order?: 'asc' | 'desc';
  per_page?: number;
  page?: number;
}

export interface ProjectStats {
  total_projects: number;
  active_projects: number;
  completed_projects: number;
  needs_review: number;
  status_breakdown: { [status: string]: number };
  priority_breakdown: { [priority: string]: number };
}

export interface AssignableUsers {
  clients: Array<{
    id: number;
    name: string;
    email: string;
  }>;
  projectManagers: Array<{
    id: number;
    name: string;
    email: string;
  }>;
  developers: Array<{
    id: number;
    name: string;
    email: string;
  }>;
}

interface ApiResponse<T = any> {
  success: boolean;
  data?: T;
  message?: string;
  errors?: { [key: string]: string };
  error_id?: string;
  meta?: any;
}

class ProjectServiceError extends Error {
  constructor(
    message: string,
    public statusCode?: number,
    public errorId?: string,
    public validationErrors?: { [key: string]: string }
  ) {
    super(message);
    this.name = 'ProjectServiceError';
  }
}

export const useProjectService = () => {
  const baseUrl = process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1';

  const makeRequest = useCallback(async <T = any>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<ApiResponse<T>> => {
    const startTime = performance.now();

    // Get auth token from localStorage or context
    const token = localStorage.getItem('auth_token');

    const response = await fetch(`${baseUrl}${endpoint}`, {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'X-Request-ID': crypto.randomUUID(),
        ...(token && { Authorization: `Bearer ${token}` }),
        ...options.headers,
      },
    });

    const endTime = performance.now();
    const responseTime = endTime - startTime;

    // Performance monitoring for constitutional compliance
    if (responseTime > 500) {
      console.warn(`Slow API response: ${endpoint} took ${responseTime.toFixed(2)}ms`);
    }

    const data: ApiResponse<T> = await response.json();

    if (!response.ok) {
      throw new ProjectServiceError(
        data.message || 'An error occurred',
        response.status,
        data.error_id,
        data.errors
      );
    }

    if (!data.success) {
      throw new ProjectServiceError(
        data.message || 'Request failed',
        response.status,
        data.error_id,
        data.errors
      );
    }

    return data;
  }, [baseUrl]);

  /**
   * Get projects list with filtering and pagination (T034: ProjectController)
   */
  const getProjects = useCallback(async (filters: ProjectFilters = {}): Promise<Project[]> => {
    try {
      const queryParams = new URLSearchParams();
      
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          queryParams.append(key, String(value));
        }
      });

      const response = await makeRequest<{ projects: Project[]; summary?: any }>(`/projects?${queryParams.toString()}`);

      // Extract projects array from the nested response structure
      return response.data?.projects || [];
    } catch (error) {
      if (error instanceof ProjectServiceError) {
        throw error;
      }
      throw new ProjectServiceError('Failed to retrieve projects');
    }
  }, [makeRequest]);

  /**
   * Get single project details (T034: ProjectController)
   */
  const getProject = useCallback(async (projectId: number): Promise<Project> => {
    try {
      const response = await makeRequest<Project>(`/projects/${projectId}`);
      return response.data as Project;
    } catch (error) {
      if (error instanceof ProjectServiceError) {
        throw error;
      }
      throw new ProjectServiceError('Failed to retrieve project');
    }
  }, [makeRequest]);

  /**
   * Create new project (T034: ProjectController)
   */
  const createProject = useCallback(async (projectData: Partial<Project>): Promise<Project> => {
    try {
      const response = await makeRequest<Project>('/projects', {
        method: 'POST',
        body: JSON.stringify(projectData),
      });

      console.log('Project created:', response.data);
      return response.data as Project;
    } catch (error) {
      if (error instanceof ProjectServiceError) {
        throw error;
      }
      throw new ProjectServiceError('Failed to create project');
    }
  }, [makeRequest]);

  /**
   * Update project (T034: ProjectController)
   */
  const updateProject = useCallback(async (
    projectId: number,
    projectData: Partial<Project>
  ): Promise<Project> => {
    try {
      const response = await makeRequest<Project>(`/projects/${projectId}`, {
        method: 'PUT',
        body: JSON.stringify(projectData),
      });

      console.log('Project updated:', projectId);
      return response.data as Project;
    } catch (error) {
      if (error instanceof ProjectServiceError) {
        throw error;
      }
      throw new ProjectServiceError('Failed to update project');
    }
  }, [makeRequest]);

  /**
   * Update project status (T034: ProjectController)
   */
  const updateProjectStatus = useCallback(async (
    projectId: number,
    status: string,
    reason?: string
  ): Promise<Project> => {
    try {
      const response = await makeRequest<Project>(`/projects/${projectId}/status`, {
        method: 'PATCH',
        body: JSON.stringify({ status, reason }),
      });

      console.log('Project status updated:', projectId, status);
      return response.data as Project;
    } catch (error) {
      if (error instanceof ProjectServiceError) {
        throw error;
      }
      throw new ProjectServiceError('Failed to update project status');
    }
  }, [makeRequest]);

  /**
   * Delete project (T034: ProjectController)
   */
  const deleteProject = useCallback(async (projectId: number): Promise<void> => {
    try {
      await makeRequest(`/projects/${projectId}`, {
        method: 'DELETE',
      });

      console.log('Project deleted:', projectId);
    } catch (error) {
      if (error instanceof ProjectServiceError) {
        throw error;
      }
      throw new ProjectServiceError('Failed to delete project');
    }
  }, [makeRequest]);

  /**
   * Get project statistics (T034: ProjectController)
   */
  const getProjectStatistics = useCallback(async (): Promise<ProjectStats> => {
    try {
      const response = await makeRequest<ProjectStats>('/projects/statistics');
      return response.data as ProjectStats;
    } catch (error) {
      if (error instanceof ProjectServiceError) {
        throw error;
      }
      throw new ProjectServiceError('Failed to retrieve project statistics');
    }
  }, [makeRequest]);

  /**
   * Get assignable users for project management (Admin only)
   */
  const getAssignableUsers = useCallback(async (): Promise<AssignableUsers> => {
    try {
      const response = await makeRequest<AssignableUsers>('/admin/projects/assignable-users');
      return response.data as AssignableUsers;
    } catch (error) {
      if (error instanceof ProjectServiceError) {
        throw error;
      }
      throw new ProjectServiceError('Failed to retrieve assignable users');
    }
  }, [makeRequest]);

  /**
   * Create project from onboarding session (T034: ProjectController)
   */
  const createProjectFromOnboarding = useCallback(async (onboardingSessionId: number): Promise<Project> => {
    try {
      const response = await makeRequest<Project>('/projects/from-onboarding', {
        method: 'POST',
        body: JSON.stringify({ onboarding_session_id: onboardingSessionId }),
      });

      console.log('Project created from onboarding:', onboardingSessionId);
      return response.data as Project;
    } catch (error) {
      if (error instanceof ProjectServiceError) {
        throw error;
      }
      throw new ProjectServiceError('Failed to create project from onboarding');
    }
  }, [makeRequest]);

  /**
   * Validate project data locally
   */
  const validateProjectData = useCallback((data: Partial<Project>): {
    isValid: boolean;
    errors: { [key: string]: string };
  } => {
    const errors: { [key: string]: string } = {};

    if (!data.name?.trim()) {
      errors.name = 'Project name is required';
    } else if (data.name.length < 3) {
      errors.name = 'Project name must be at least 3 characters';
    } else if (data.name.length > 255) {
      errors.name = 'Project name must be less than 255 characters';
    }

    if (!data.description?.trim()) {
      errors.description = 'Project description is required';
    } else if (data.description.length < 20) {
      errors.description = 'Description must be at least 20 characters';
    } else if (data.description.length > 5000) {
      errors.description = 'Description must be less than 5000 characters';
    }

    if (!data.project_types?.length) {
      errors.project_types = 'Please select at least one project type';
    }

    if (!data.priority) {
      errors.priority = 'Please select a priority level';
    }

    if (data.expected_start_date && data.expected_end_date) {
      const startDate = new Date(data.expected_start_date);
      const endDate = new Date(data.expected_end_date);
      if (endDate <= startDate) {
        errors.expected_end_date = 'End date must be after start date';
      }
    }

    return {
      isValid: Object.keys(errors).length === 0,
      errors
    };
  }, []);

  /**
   * Get available options for project fields
   */
  const getProjectOptions = useCallback(() => {
    return {
      projectTypes: [
        { value: 'web_app', label: 'Web Application' },
        { value: 'mobile_app', label: 'Mobile Application' },
        { value: 'desktop_app', label: 'Desktop Application' },
        { value: 'api', label: 'API/Backend Service' },
        { value: 'website', label: 'Corporate Website' },
        { value: 'ecommerce', label: 'E-commerce Platform' },
        { value: 'crm', label: 'CRM System' },
        { value: 'cms', label: 'Content Management System' },
        { value: 'analytics', label: 'Analytics Dashboard' },
        { value: 'other', label: 'Other' }
      ],
      statuses: [
        { value: 'draft', label: 'Draft' },
        { value: 'submitted', label: 'Submitted' },
        { value: 'in_review', label: 'In Review' },
        { value: 'approved', label: 'Approved' },
        { value: 'in_progress', label: 'In Progress' },
        { value: 'completed', label: 'Completed' },
        { value: 'closed', label: 'Closed' },
        { value: 'cancelled', label: 'Cancelled' }
      ],
      priorityLevels: [
        { value: 'low', label: 'Low Priority' },
        { value: 'normal', label: 'Normal Priority' },
        { value: 'high', label: 'High Priority' },
        { value: 'urgent', label: 'Urgent' }
      ],
      budgetRanges: [
        { value: 'under_5k', label: 'Under $5,000' },
        { value: '5k_to_15k', label: '$5,000 - $15,000' },
        { value: '15k_to_30k', label: '$15,000 - $30,000' },
        { value: '30k_to_50k', label: '$30,000 - $50,000' },
        { value: '50k_to_100k', label: '$50,000 - $100,000' },
        { value: 'over_100k', label: 'Over $100,000' }
      ],
      timelines: [
        { value: 'under_1_month', label: 'Under 1 month' },
        { value: '1_to_3_months', label: '1-3 months' },
        { value: '3_to_6_months', label: '3-6 months' },
        { value: '6_to_12_months', label: '6-12 months' },
        { value: 'over_12_months', label: 'Over 12 months' }
      ]
    };
  }, []);

  return {
    // Core CRUD operations
    getProjects,
    getProject,
    createProject,
    updateProject,
    updateProjectStatus,
    deleteProject,
    
    // Statistics and derived data
    getProjectStatistics,
    getAssignableUsers,
    createProjectFromOnboarding,
    
    // Validation and options
    validateProjectData,
    getProjectOptions,
    ProjectServiceError
  };
};

export default useProjectService;